// frontend/src/components/announcements/AnnouncementFormModal.jsx
import { useState } from "react";
import { useApp } from "../../store";
import { Icon } from "../Common";
import MemberPicker from "../common/MemberPicker";
import { createAnnouncement, updateAnnouncement } from "../../lib/api";

// Convert a UTC ISO timestamp to the local `YYYY-MM-DDTHH:mm` value a
// datetime-local input expects. Mirrors the submit path (`new Date(value)
// .toISOString()`), which reads the input as local time — so load and save
// stay symmetric instead of being off by the browser's timezone offset.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function AnnouncementFormModal({ announcement, onSaved, onClose }) {
  const { toast } = useApp();
  const isEdit = !!announcement;
  // Once delivered, recipients + schedule are frozen (server ignores edits too).
  const dispatched = !!announcement?.dispatchedAt;

  const [title, setTitle] = useState(announcement?.title || "");
  const [body, setBody] = useState(announcement?.body || "");
  const [pinned, setPinned] = useState(announcement?.pinned || false);
  const [expiresAt, setExpiresAt] = useState(toLocalInput(announcement?.expiresAt));

  // Audience: everyone vs a chosen set of employees/managers.
  const [audience, setAudience] = useState(
    announcement?.recipients?.length ? "specific" : "all",
  );
  const [selected, setSelected] = useState(
    () => new Set((announcement?.recipients || []).map((r) => r.id)),
  );

  // Delivery: send now vs schedule for a future date/time.
  const [delivery, setDelivery] = useState(announcement?.scheduledAt ? "schedule" : "now");
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(announcement?.scheduledAt));

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required";
    if (!body.trim()) e.body = "Body is required";
    if (title.length > 200) e.title = "Title too long (max 200 chars)";
    if (body.length > 10000) e.body = "Body too long (max 10000 chars)";
    if (expiresAt) {
      const d = new Date(expiresAt);
      if (isNaN(d.getTime())) e.expiresAt = "Invalid date";
      else if (d <= new Date()) e.expiresAt = "Must be in the future";
    }
    if (!dispatched && audience === "specific" && selected.size === 0) {
      e.recipients = "Select at least one person, or choose Everyone";
    }
    if (!dispatched && delivery === "schedule") {
      if (!scheduledAt) e.scheduledAt = "Pick a date & time";
      else {
        const d = new Date(scheduledAt);
        if (isNaN(d.getTime())) e.scheduledAt = "Invalid date";
        else if (d <= new Date()) e.scheduledAt = "Must be in the future";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || saving) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        pinned,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      };
      // Recipients + schedule are only meaningful before dispatch.
      if (!dispatched) {
        payload.recipientIds = audience === "specific" ? [...selected] : [];
        payload.scheduledAt =
          delivery === "schedule" && scheduledAt ? new Date(scheduledAt).toISOString() : null;
      }

      if (isEdit) {
        await updateAnnouncement(announcement.id, payload);
      } else {
        await createAnnouncement(payload);
      }

      onSaved?.();
      onClose?.();
    } catch (e) {
      if (e.details?.fieldErrors) {
        const fieldErrors = {};
        for (const [key, msgs] of Object.entries(e.details.fieldErrors)) {
          if (msgs?.length) fieldErrors[key] = msgs[0];
        }
        setErrors(fieldErrors);
      } else if (e.message === "invalid_recipients") {
        setErrors({ recipients: "One or more selected people are no longer valid" });
      } else {
        toast(e.message || "Failed to save announcement", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const submitLabel = isEdit
    ? "Update"
    : delivery === "schedule"
      ? "Schedule"
      : "Publish";

  return (
    <div>
      <div className="modal-head">
        <h2>{isEdit ? "Edit Announcement" : "New Announcement"}</h2>
      </div>
      <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
        <div className="field">
          <label>Title *</label>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Announcement title"
            maxLength={200}
          />
          {errors.title && <small style={{ color: "var(--error)" }}>{errors.title}</small>}
          <small style={{ color: "var(--text-muted)" }}>{title.length}/200</small>
        </div>

        <div className="field">
          <label>Body *</label>
          <textarea
            className="input"
            rows={8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your announcement here…"
            maxLength={10000}
          />
          {errors.body && <small style={{ color: "var(--error)" }}>{errors.body}</small>}
          <small style={{ color: "var(--text-muted)" }}>{body.length}/10000</small>
        </div>

        {/* ── Audience ─────────────────────────────────────────────── */}
        <div className="field">
          <label>Send to</label>
          {dispatched ? (
            <small style={{ color: "var(--text-muted)" }}>
              {announcement.recipients?.length
                ? `${announcement.recipients.length} selected ${announcement.recipients.length === 1 ? "person" : "people"}`
                : "Everyone in the company"}
            </small>
          ) : (
            <>
              <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="audience"
                    checked={audience === "all"}
                    onChange={() => setAudience("all")}
                  />
                  <span>Everyone</span>
                </label>
                <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="audience"
                    checked={audience === "specific"}
                    onChange={() => setAudience("specific")}
                  />
                  <span>Specific people</span>
                </label>
              </div>
              {audience === "specific" && (
                <MemberPicker selected={selected} onChange={setSelected} />
              )}
              {errors.recipients && (
                <small style={{ color: "var(--error)" }}>{errors.recipients}</small>
              )}
            </>
          )}
        </div>

        {/* ── Delivery ─────────────────────────────────────────────── */}
        <div className="field">
          <label>Delivery</label>
          {dispatched ? (
            <small style={{ color: "var(--text-muted)", display: "block" }}>
              {announcement.scheduledAt && (
                <>
                  Scheduled for{" "}
                  {new Date(announcement.scheduledAt).toLocaleString("en-IN", {
                    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                  {" · "}
                </>
              )}
              Already sent on{" "}
              {new Date(announcement.dispatchedAt).toLocaleString("en-IN", {
                day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
              })}
            </small>
          ) : (
            <>
              <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="delivery"
                    checked={delivery === "now"}
                    onChange={() => setDelivery("now")}
                  />
                  <span>Send now</span>
                </label>
                <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="delivery"
                    checked={delivery === "schedule"}
                    onChange={() => setDelivery("schedule")}
                  />
                  <span>Schedule</span>
                </label>
              </div>
              {delivery === "schedule" && (
                <>
                  <input
                    type="datetime-local"
                    className="input"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    style={{ maxWidth: 260 }}
                  />
                  {errors.scheduledAt && (
                    <small style={{ color: "var(--error)" }}>{errors.scheduledAt}</small>
                  )}
                  <small style={{ color: "var(--text-muted)", display: "block" }}>
                    The announcement is delivered automatically at this time (WhatsApp + email, per your Announcement Settings).
                  </small>
                </>
              )}
            </>
          )}
        </div>

        <div className="field">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              <span>Pin this announcement</span>
            </label>
          </div>
          <small style={{ color: "var(--text-muted)" }}>
            Pin to the top of dashboards and reminders. Does not affect who receives it.
          </small>
        </div>

        <div className="field">
          <label>Expiry (optional)</label>
          <input
            type="datetime-local"
            className="input"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            style={{ maxWidth: 260 }}
          />
          {errors.expiresAt && <small style={{ color: "var(--error)" }}>{errors.expiresAt}</small>}
          <small style={{ color: "var(--text-muted)" }}>
            Leave empty for no expiry. Expired announcements are automatically hidden.
          </small>
        </div>
      </div>
      <div className="modal-foot" style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
          <Icon name="check" size={16} /> {saving ? "Saving…" : submitLabel}
        </button>
      </div>
    </div>
  );
}
